import { css } from 'aphrodite';
import Linkify from 'linkify-react';
import type { Opts } from 'linkifyjs';
import * as React from 'react';
import { IFacebookPageLookupResult, LogoPosition } from '../../../../extViewmodels';
import { getSocialMediaPlatformLogoUrl } from '../../../../models/UiUtils';
import {
	getFacebookMentionRanges,
	getMentionSegments,
	stripMentionMarkers,
} from '../../../../models/utils/socialUtils';
import { IFileAttachment, IPostTarget, SocialMediaType } from '../../../../view/models/Api';
import videoPreviewURL from '../../../assets/video_preview.png';
import { brandSecondary } from '../../../styles/colors';
import { baseStyleSheet } from '../../../styles/styles';
import { DeprecatedCloseButton } from '../../DeprecatedCloseButton';
import { RadioButton } from '../../RadioButton';
import { DisclosureIcon } from '../../svgs/icons/DisclosureIcon';
import { styleSheet } from './styles';

export const MainImageWidth = 372;
export const HalfImageWidth = MainImageWidth / 2 - 1;
export const ThirdImageWidth = MainImageWidth / 3 - 2;
export const MainImageHeight = 250;
export const HalfImageHeight = 155;
export const SmallImageHeight = 210;

export enum PreviewSize {
	Md = 'md',
	Sm = 'sm',
}
interface IProps {
	facebookMentions?: IFacebookPageLookupResult[];
	/** A hidden copy that only holds the card's height, so it carries none of the visible card's test ids. */
	isSizer?: boolean;
	previewContent?: string;
	postContent?: string;
	/** Collapsed content of the platform that shows the most, which reserves the caption's height. */
	sizerContent?: string;
	sizerTruncated?: boolean;
	target?: IPostTarget;
	truncate?: boolean;
}

export const PreviewPostContentView = ({
	facebookMentions,
	isSizer,
	postContent,
	previewContent,
	sizerContent,
	sizerTruncated,
	target,
	truncate,
}: IProps) => {
	const [showAll, setShowAll] = React.useState(false);
	const onShowAll = () => {
		setShowAll(!showAll);
	};

	// `ignoreTags` matches react-linkify's built-in skip list, so anchors/buttons
	// already present in the post content don't get a second <a> nested inside them.
	const linkifyOptions: Opts = {
		ignoreTags: ['a', 'button'],
		render: ({ attributes, content }) => (
			<a {...attributes} className={css(baseStyleSheet.brandLink)} target='_blank' rel='noreferrer'>
				{content}
			</a>
		),
	};

	const displayedContent = showAll ? postContent : previewContent;

	const mentionRanges = React.useMemo(
		() => getFacebookMentionRanges(displayedContent, facebookMentions),
		[displayedContent, facebookMentions]
	);
	const mentionSegments = React.useMemo(
		() => getMentionSegments(displayedContent, mentionRanges),
		[displayedContent, mentionRanges]
	);
	const hasMentionHighlights = mentionSegments.some(segment => segment.isMention);
	const isFacebookTarget = target?.provider === SocialMediaType.Facebook;

	// Only Facebook tags a Page; the other networks publish the name on its own.
	const renderedContent =
		hasMentionHighlights && !isFacebookTarget
			? stripMentionMarkers(displayedContent, mentionRanges)
			: hasMentionHighlights
				? mentionSegments.map((segment, index) =>
						segment.isMention ? (
							<span
								className={css(styleSheet.mentionHighlight)}
								data-testid={isSizer ? undefined : 'facebook-mention-highlight'}
								key={`mention-${index}`}
							>
								{segment.text}
							</span>
						) : (
							<React.Fragment key={`text-${index}`}>{segment.text}</React.Fragment>
						)
					)
				: displayedContent;

	return (
		<div className={css(styleSheet.textOnlyPadding, styleSheet.captionArea)}>
			{sizerContent ? (
				<div aria-hidden className={css(styleSheet.captionSizer)} data-testid={isSizer ? undefined : 'caption-sizer'}>
					{sizerContent}
					{sizerTruncated ? <span className={css(baseStyleSheet.fontBold)}>... Read more</span> : null}
				</div>
			) : null}
			<div className={css(styleSheet.captionLayer)} data-testid={isSizer ? undefined : 'caption'}>
				{target?.provider === SocialMediaType.Facebook || target?.provider === SocialMediaType.LinkedIn ? (
					<Linkify options={linkifyOptions}>{renderedContent}</Linkify>
				) : (
					<>{renderedContent}</>
				)}
				{previewContent?.length > 0 &&
					truncate &&
					(showAll ? (
						<DeprecatedCloseButton className={css(styleSheet.closeButton)} fillColor='black' onClick={onShowAll} />
					) : (
						<button onClick={onShowAll}>
							<span className={css(baseStyleSheet.fontBold)}>... Read more</span>
						</button>
					))}
			</div>
		</div>
	);
};
interface IPlatformPreviewImageViewProps {
	/** @see IProps.isSizer */
	isSizer?: boolean;
	onCtaClick?: () => void;
	onClickEnlargeImage?: (imageUrl: string) => void;
	previewSize?: PreviewSize;
	postImages?: IFileAttachment[];
	controls?: boolean;
	logoPosition?: LogoPosition;
}

export const InstagramImagePreview: React.FC<IPlatformPreviewImageViewProps> = props => {
	const { postImages, previewSize, onCtaClick, onClickEnlargeImage } = props;
	const [selectedImage, setSelectedImage] = React.useState(0);
	// Each dot's label points at its input by id, and one image can appear in several previews at once.
	const dotIdPrefix = React.useId();

	const onSelectedTabChanged = (indx: number) => () => {
		setSelectedImage(indx);
	};
	const count = postImages?.length ?? 0;
	if (!count) {
		return null;
	}
	// Removing media from the version on screen can leave the selection past the end of the list.
	const shownIndex = Math.min(selectedImage, count - 1);
	const shownImage = postImages[shownIndex];
	const isMultiple = count > 1;
	const imgHeight = previewSize === PreviewSize.Sm ? SmallImageHeight : !isMultiple ? 'auto' : MainImageHeight;
	return (
		<>
			<div
				className={css(
					previewSize !== PreviewSize.Sm ? styleSheet.imageDisplayAreaPreviewNotMultiple : undefined,
					styleSheet.imageDisplayAreaPreview,
					styleSheet.imageDisplayAreaPreviewInstagram
				)}
				style={{ position: 'relative' }}
			>
				<img
					className={css(styleSheet.tab)}
					onClick={onCtaClick ? onCtaClick : () => onClickEnlargeImage(shownImage.url)}
					src={shownImage.url}
					width={MainImageWidth}
					height={imgHeight}
				/>
			</div>
			<div
				className={`${css(
					styleSheet.tabNavigation,
					previewSize === PreviewSize.Sm ? styleSheet.radioBackground : undefined
				)}`}
			>
				{postImages.map((image, i) => {
					return (
						<RadioButton
							checked={i === shownIndex}
							className={css(styleSheet.circle)}
							id={`${dotIdPrefix}-${i}`}
							key={`${image.url}-${i}`}
							onChange={onSelectedTabChanged(i)}
						/>
					);
				})}
			</div>
		</>
	);
};

export const BasicImagePreview: React.FC<IPlatformPreviewImageViewProps> = ({
	postImages,
	previewSize,
	onCtaClick,
	onClickEnlargeImage,
}) => {
	const count = postImages?.length;
	const isMultiple = count > 1;
	const imgHeight = previewSize === PreviewSize.Sm ? SmallImageHeight : !isMultiple ? 'auto' : MainImageHeight;

	return (
		<div
			className={css(
				styleSheet.imageDisplayAreaPreview,
				previewSize !== PreviewSize.Sm && isMultiple
					? styleSheet.imageDisplayAreaPreviewNotMultiple
					: styleSheet.imageDisplayAreaPreviewMultiple,
				count > 4 && styleSheet.imageDisplayAreaPreviewColWrap,
				previewSize === PreviewSize.Sm ? styleSheet.clickNav : undefined
			)}
		>
			{postImages.map((image, i) => {
				if (((count === 2 || count === 3 || count === 4) && i === 0) || !isMultiple) {
					return (
						<button onClick={onCtaClick || (() => onClickEnlargeImage?.(image.url))} key={`${image.url}-${i}`}>
							<img src={image.url} width={MainImageWidth} height={imgHeight} />
						</button>
					);
				}

				if (count === 3) {
					return (
						<button
							className={css(i === 2 && styleSheet.imageDisplayLeft)}
							onClick={onCtaClick || (() => onClickEnlargeImage?.(image.url))}
							key={`${image.url}-${i}`}
						>
							<img src={image.url} width={HalfImageWidth} height={HalfImageHeight} />
						</button>
					);
				}

				if (count === 2) {
					return (
						<button onClick={onCtaClick || (() => onClickEnlargeImage?.(image.url))} key={`${image.url}-${i}`}>
							<img src={image.url} width={MainImageWidth} height={MainImageHeight} />
						</button>
					);
				}

				if (count === 4) {
					return (
						<button
							className={css((i === 3 || i === 2) && styleSheet.imageDisplayLeft)}
							onClick={onCtaClick || (() => onClickEnlargeImage?.(image.url))}
							key={`${image.url}-${i}`}
						>
							<img
								src={image.url}
								width={i === 3 ? ThirdImageWidth : ThirdImageWidth + 1}
								height={HalfImageHeight} // These are reverse because of col wrap
							/>
						</button>
					);
				}

				return (
					<button
						className={css((i === 2 || i === 3 || i === 4) && styleSheet.imageDisplayLeft)}
						onClick={onCtaClick || (() => onClickEnlargeImage?.(image.url))}
						key={`${image.url}-${i}`}
					>
						<>
							<img
								src={image.url}
								width={HalfImageWidth}
								height={i === 0 || i === 1 ? HalfImageWidth - 1 : ThirdImageWidth} // These are reverse because of col wrap
							/>
						</>
					</button>
				);
			})}
		</div>
	);
};

// LinkedIn crops a multi-image post into one 16:9 area: the first image on the left at two thirds of
// the width, the rest stacked beside it, four tiles at most with a +N badge on the last. Measured off
// live posts - a 555x310 block split 369/186 - so the preview stands as tall as the published post.
const LinkedInBlockAspect = 310 / 555;
const LinkedInLeadShare = 369 / 555;
const LinkedInMaxTiles = 4;

const LinkedInImageGrid = ({ isSizer, postImages, onClickEnlargeImage }: IPlatformPreviewImageViewProps) => {
	const blockHeight = Math.round(MainImageWidth * LinkedInBlockAspect);
	const leadWidth = Math.round(MainImageWidth * LinkedInLeadShare);
	const stackedImages = postImages.slice(1, LinkedInMaxTiles);
	const hiddenCount = postImages.length - LinkedInMaxTiles;

	return (
		<div
			className={css(styleSheet.linkedInImageGrid)}
			style={{ height: blockHeight, width: MainImageWidth }}
			data-testid={isSizer ? undefined : 'linkedin-image-grid'}
		>
			<button
				className={css(styleSheet.linkedInImageLead)}
				onClick={() => onClickEnlargeImage?.(postImages[0].url)}
				style={{ width: leadWidth }}
			>
				<img src={postImages[0].url} />
			</button>
			<div className={css(styleSheet.linkedInImageStack)}>
				{stackedImages.map((image, i) => (
					<button
						className={css(styleSheet.linkedInImageTile)}
						key={`${image.url}-${i}`}
						onClick={() => onClickEnlargeImage?.(image.url)}
					>
						<img src={image.url} />
						{i === stackedImages.length - 1 && hiddenCount > 0 ? (
							<span className={css(styleSheet.linkedInHiddenCount)}>{`+${hiddenCount}`}</span>
						) : null}
					</button>
				))}
			</div>
		</div>
	);
};

export const LinkedInImagePreview: React.FC<IPlatformPreviewImageViewProps> = ({
	isSizer,
	postImages,
	previewSize,
	onClickEnlargeImage,
}) => {
	const count = postImages?.length;

	if (count > 2) {
		return <LinkedInImageGrid isSizer={isSizer} onClickEnlargeImage={onClickEnlargeImage} postImages={postImages} />;
	}

	const isMultiple = count > 1;
	const imgHeight = previewSize === PreviewSize.Sm ? SmallImageHeight : !isMultiple ? 'auto' : 476;

	return (
		<div className={css(styleSheet.imageDisplayAreaPreviewNotMultiple, styleSheet.imageDisplayAreaPreview)}>
			{postImages.map((image, i) => (
				<button key={`${image.url}-${i}`} onClick={() => onClickEnlargeImage(image.url)}>
					<img src={image.url} width={isMultiple ? HalfImageWidth : MainImageWidth} height={imgHeight} />
				</button>
			))}
		</div>
	);
};
export const BasicVideoPreview: React.FC<IPlatformPreviewImageViewProps> = ({
	isSizer,
	postImages,
	previewSize,
	onCtaClick,
	controls,
}) => {
	return (
		<div
			className={css(
				styleSheet.imageDisplayAreaPreview,
				previewSize !== PreviewSize.Sm
					? styleSheet.imageDisplayAreaPreviewNotMultiple
					: styleSheet.imageDisplayAreaPreviewMultiple,
				previewSize === PreviewSize.Sm ? styleSheet.clickNav : undefined
			)}
			onClick={previewSize === PreviewSize.Sm ? onCtaClick : null}
		>
			{postImages.map((image, i) => {
				return (
					<div className={css(styleSheet.videoWrapper)} key={`${image.url}-${i}`}>
						{controls ? (
							<video
								key={`${image.url}-${i}`}
								src={image.url}
								width={MainImageWidth}
								height={MainImageHeight} // These are reverse because of col wrap
								controls={controls}
								// The width and height fix the box, so a copy that only holds space fetches nothing.
								preload={isSizer ? 'none' : undefined}
							/>
						) : (
							<img src={videoPreviewURL} width='100%' />
						)}
					</div>
				);
			})}
		</div>
	);
};

export const PreviewImages = ({
	isSizer,
	postTargets,
	targetIndex,
	postImages,
	displayContent,
	postContent,
	contentDisplayLength,
	facebookMentions,
	onClickEnlargeImage,
	disclosureContent,
	sizerContent,
	sizerTruncated,
}: {
	/** @see IProps.isSizer */
	isSizer?: boolean;
	postTargets: IPostTarget[];
	targetIndex: number;
	postImages: IFileAttachment[];
	displayContent: string;
	postContent: string;
	contentDisplayLength: number;
	facebookMentions?: IFacebookPageLookupResult[];
	onClickEnlargeImage: (imageUrl: string) => void;
	disclosureContent?: string | null;
	sizerContent?: string;
	sizerTruncated?: boolean;
}) => {
	return (
		<figure
			className={css(
				styleSheet.previewContentBox,
				postTargets?.[targetIndex]?.provider === SocialMediaType.Instagram &&
					postImages?.length > 1 &&
					styleSheet.previewContentBoxInstagram
			)}
		>
			<PreviewPostContentView
				facebookMentions={facebookMentions}
				isSizer={isSizer}
				previewContent={displayContent}
				postContent={postContent}
				sizerContent={sizerContent}
				sizerTruncated={sizerTruncated}
				truncate={postContent?.length > contentDisplayLength}
				target={postTargets?.[targetIndex]}
			/>
			{disclosureContent && <div className={css(styleSheet.disclosureContent)}>{disclosureContent}</div>}
			{postImages?.length > 0 ? (
				postImages.some(x => x.mimeType?.startsWith('video/')) ? (
					<BasicVideoPreview controls={true} isSizer={isSizer} postImages={postImages} />
				) : postTargets?.[targetIndex]?.provider === SocialMediaType.Instagram && postImages?.length > 1 ? (
					<InstagramImagePreview postImages={postImages} onClickEnlargeImage={onClickEnlargeImage} />
				) : postTargets?.[targetIndex]?.provider === SocialMediaType.LinkedIn ? (
					<LinkedInImagePreview isSizer={isSizer} postImages={postImages} onClickEnlargeImage={onClickEnlargeImage} />
				) : (
					<BasicImagePreview postImages={postImages} onClickEnlargeImage={onClickEnlargeImage} />
				)
			) : null}
		</figure>
	);
};

export const PreviewPostTargets = ({
	postTargets,
	onSetTargetPreview,
	targetIndex,
}: {
	postTargets: IPostTarget[];
	onSetTargetPreview: (index?: number) => () => void;
	targetIndex: number;
}) => {
	return (
		<p className={css(styleSheet.iconRow)}>
			{postTargets?.length > 1 && (
				<button onClick={onSetTargetPreview(Math.max(targetIndex - 1, 0))}>
					<DisclosureIcon fillColor={brandSecondary} type='chevron' className={css(styleSheet.pointLeft)} />
				</button>
			)}
			{postTargets?.map((x, i) => (
				<span className={css(styleSheet.iconRow)} key={`${x}-${i}`}>
					<button
						className={
							x === postTargets?.[targetIndex]
								? css(styleSheet.iconRow, styleSheet.iconRowSelected)
								: css(styleSheet.iconRow)
						}
						onClick={onSetTargetPreview(i)}
					>
						<img key={i} src={getSocialMediaPlatformLogoUrl(x.provider)} />
					</button>
				</span>
			))}
			{postTargets?.length > 1 && (
				<button onClick={onSetTargetPreview(Math.min(targetIndex + 1, postTargets.length - 1))}>
					<DisclosureIcon fillColor={brandSecondary} type='chevron' className={css(styleSheet.pointRight)} />
				</button>
			)}
		</p>
	);
};
